<template>
  <Card :id="id" tabindex="-1" class="base-shipment-timeline-item">
    <template #content>
      <div class="header-row">
        <div class="header-row-left">
          <div class="flex flex-gap-1 align-items-center">
            <div class="event-type-wrapper">
              <slot name="eventTypeIcon" />

              <span class="event-type" :title="eventType">
                {{ formattedEventType || eventType }}
              </span>
            </div>

            <slot name="eventTypeChip" />
          </div>

          <div class="subheader">{{ subheader }}</div>
        </div>

        <div class="header-row-right">
          <KeyValuePair class="date" :label="dateLabel" :value="formattedDate">
            <template #label="{ label }">
              {{ label }}

              <RygenChip
                v-if="late"
                :label="t('common.late')"
                :severity="Severity.WARNING"
              />
            </template>
          </KeyValuePair>

          <EllipsisMenu
            v-if="!hideMenu"
            :disabled="disabled"
            :menu-items="menuItems"
            hide-on-empty
          />
        </div>
      </div>

      <div v-if="comment" class="body-row comments">
        <KeyValuePair :label="t('common.comments')" :value="comment" />
      </div>

      <div v-if="slots.extraRow" class="body-row extra">
        <slot name="extraRow" :disabled="disabled" :late="late" />
      </div>
    </template>
  </Card>
</template>

<script lang="ts" setup>
import type { PropType } from 'vue';
import { toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { RygenMenuItem, Severity } from '@rygen/rygen-ui/model';

import EllipsisMenu from '@rygen/rygen-ui/components/ui/EllipsisMenu.vue';
import KeyValuePair from '@rygen/rygen-ui/components/ui/KeyValuePair.vue';
import RygenChip from '@rygen/rygen-ui/components/ui/RygenChip.vue';
import Card from 'primevue/card';

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  eventType: {
    type: String,
    default: () => '',
  },
  formattedEventType: {
    type: String,
    default: () => '',
  },
  subheader: {
    type: String,
    default: () => '',
  },
  dateLabel: {
    type: String,
    default: () => '',
  },
  formattedDate: {
    type: String,
    default: () => '',
  },
  menuItems: {
    type: Array as PropType<RygenMenuItem[]>,
    default: () => [],
  },
  comment: {
    type: String,
    default: () => '',
  },
  late: {
    type: Boolean,
    default: () => false,
  },
  disabled: {
    type: Boolean,
    default: () => false,
  },
  hideMenu: {
    type: Boolean,
    default: () => false,
  },
});
const {
  id,
  eventType,
  formattedEventType,
  subheader,
  dateLabel,
  formattedDate,
  menuItems,
  comment,
  late,
  disabled,
  hideMenu,
} = toRefs(props);

const slots = defineSlots<{
  eventTypeChip: () => any;
  eventTypeIcon: () => any;
  extraRow: (props: { disabled: boolean; late: boolean }) => any;
}>();

const { t } = useI18n();
</script>

<style>
:root {
  --base-shipment-timeline-item-header-color: var(--p-gray-800);
  --base-shipment-timeline-item-header-font-size: 14px;
  --base-shipment-timeline-item-date-width: 200px;
  --base-shipment-timeline-item-subheader-color: var(--p-gray-400);
  --base-shipment-timeline-item-body-row-background-color: var(--p-gray-50);
}

.base-shipment-timeline-item.p-card {
  --p-card-body-padding: 0;

  .header-row {
    display: flex;
    gap: var(--gap-1);
    justify-content: space-between;
    align-items: center;
    padding: var(--sp-3) var(--sp-6);

    .header-row-left {
      flex: 1 0 auto;
      display: flex;
      flex-direction: column;
      gap: var(--sp-1);

      .event-type-wrapper {
        display: flex;
        gap: var(--sp-1);
        align-items: center;
        font-size: var(--base-shipment-timeline-item-header-font-size);

        .event-type {
          color: var(--base-shipment-timeline-item-header-color);
          text-overflow: ellipsis;
          overflow: hidden;
          white-space: nowrap;
        }
      }

      .subheader {
        color: var(--base-shipment-timeline-item-subheader-color);
      }
    }

    .header-row-right {
      flex: 1 0 auto;
      display: flex;
      gap: var(--sp-12);
      align-items: center;
      justify-content: end;

      .date {
        width: var(--base-shipment-timeline-item-date-width);

        .key-value-pair-label {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .key-value-pair-value {
          color: var(--p-gray-600);
          white-space: break-spaces;
        }
      }
    }
  }

  .body-row {
    display: flex;
    border-top: 1px solid var(--divider-color);
    background-color: var(
      --base-shipment-timeline-item-body-row-background-color
    );
    padding: var(--sp-2) var(--sp-6);

    &:last-of-type {
      border-bottom-left-radius: var(--p-card-border-radius);
      border-bottom-right-radius: var(--p-card-border-radius);
    }

    .key-value-pair {
      .key-value-pair-value {
        color: var(--p-gray-600);
        white-space: break-spaces;
      }
    }
  }
}
</style>
